import { LegalPageLayout } from './LegalPageLayout';
import { legal } from './legalConfig';

function Pending({ label }: { label: string }) {
  return <span className="legal-pending" title="Pending legal confirmation">{label}</span>;
}

function EffectiveDate() {
  if (legal.effectiveDate) return <>{legal.effectiveDate}</>;
  return <Pending label="Pending approval" />;
}

export function TermsOfServicePage() {
  return (
    <LegalPageLayout title="Terms of Service">
      <header className="legal-header">
        <h1>Terms of Service</h1>
        <div className="legal-meta">
          <span className="legal-meta-badge">
            Last updated: <EffectiveDate />
          </span>
          {legal.termsVersion && (
            <span className="legal-meta-badge">Version: {legal.termsVersion}</span>
          )}
          {!legal.termsVersion && (
            <span className="legal-meta-badge legal-meta-badge--pending">
              Version pending
            </span>
          )}
        </div>
      </header>

      <div className="legal-section">
        <p>
          These Terms of Service ("Terms") govern your use of the Hire It Now platform
          ("Platform") operated by {legal.companyName} ("we", "us", "our"). By creating an
          account or using the Platform, you agree to these Terms.
        </p>
        <p>
          If you are accepting these Terms on behalf of an organisation, you confirm that you
          have authority to bind that organisation.
        </p>
      </div>

      <div className="legal-section">
        <h2>1. The Platform</h2>
        <p>
          Hire It Now is a B2B SaaS platform for equipment hire coordination. It allows hiring
          companies, partners, and suppliers ("Members") to manage hire requests, jobs, orders,
          contacts, and related operational workflows.
        </p>
        <p>
          The Platform is a neutral coordination tool. {legal.companyName} is not a party to
          hire contracts, purchase contracts, transport arrangements, or other agreements made
          between Members using the Platform.
        </p>
        <p>
          Members contract directly with each other and remain solely responsible for agreeing,
          performing, and enforcing their own commercial and operational terms.
        </p>
        <p>
          Unless expressly agreed in writing, we do not act as agent, broker, reseller,
          principal contractor, or logistics provider for any Member transaction.
        </p>
        <p>
          Access to the Platform is provided on a subscription or agreed commercial basis.
          Subscription and pricing terms are set out in your order form or separate commercial
          agreement.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Accounts and Access</h2>
        <p>
          <strong>2.1</strong> You must provide accurate information when creating an account.
          You are responsible for maintaining the security of your credentials.
        </p>
        <p>
          <strong>2.2</strong> You must not share your login credentials with others. Each user
          requires their own account.
        </p>
        <p>
          <strong>2.3</strong> You are responsible for all activity that occurs under your
          account.
        </p>
        <p>
          <strong>2.4</strong> You must be at least 18 years old and have legal authority to
          enter into these Terms on your own behalf or on behalf of your organisation.
        </p>
      </div>

      <div className="legal-section">
        <h2>3. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Platform for any unlawful purpose or in violation of any applicable law or regulation</li>
          <li>Upload, transmit, or store any content that is defamatory, fraudulent, or infringes third-party rights</li>
          <li>Attempt to gain unauthorised access to any part of the Platform, other accounts, or our systems</li>
          <li>Introduce malicious code, conduct denial-of-service attacks, or otherwise interfere with Platform availability</li>
          <li>Scrape, crawl, or extract data from the Platform by automated means without our written consent</li>
          <li>Use the Platform to send unsolicited commercial communications (spam)</li>
          <li>Misrepresent your identity or your organisation's identity</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that violate these restrictions.
        </p>
      </div>

      <div className="legal-section">
        <h2>4. Your Content</h2>
        <p>
          <strong>4.1</strong> You retain ownership of data and content you submit to the
          Platform ("Your Content").
        </p>
        <p>
          <strong>4.2</strong> By submitting Your Content, you grant us a limited licence to
          store, process, and display it as necessary to provide the Platform services to you.
        </p>
        <p>
          <strong>4.3</strong> You are responsible for ensuring you have the right to submit
          any personal data about third parties (e.g. contacts you add to the Platform) and
          that doing so complies with applicable data protection law. Our{' '}
          <a href="/privacy" className="legal-link">Privacy Policy</a> explains how we handle
          that data.
        </p>
        <p>
          <strong>4.4</strong> We do not claim ownership of Your Content and will not use it
          for purposes beyond providing the Platform, as described in our Privacy Policy.
        </p>
        <p>
          <strong>4.5</strong> You are responsible for the completeness, legality, and accuracy
          of data entered into the Platform by your organisation and users.
        </p>
        <p>
          <strong>4.6</strong> We do not independently verify Member-provided operational data,
          including contacts, job details, order information, equipment availability, delivery
          information, or pricing terms.
        </p>
        <p>
          <strong>4.7</strong> You are responsible for decisions and actions taken in reliance on
          Member-provided data surfaced through the Platform.
        </p>
      </div>

      <div className="legal-section">
        <h2>5. Platform Availability and Changes</h2>
        <p>
          <strong>5.1</strong> We aim to maintain Platform availability but do not guarantee
          uninterrupted or error-free service.
        </p>
        <p>
          <strong>5.2</strong> We may update, modify, or discontinue features of the Platform
          at any time. We will provide reasonable notice of material changes where practical.
        </p>
        <p>
          <strong>5.3</strong> Planned maintenance windows will be communicated in advance
          where possible.
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Intellectual Property</h2>
        <p>
          All intellectual property in the Platform — including software, design, trademarks,
          and documentation — belongs to us or our licensors. Nothing in these Terms transfers
          any intellectual property rights to you.
        </p>
        <p>
          You may not copy, modify, distribute, or create derivative works from any part of
          the Platform without our prior written consent.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Confidentiality</h2>
        <p>
          You agree to treat any non-public information about the Platform, other Members, or
          our business that you access through the Platform as confidential, and not to
          disclose it to third parties without our consent.
        </p>
      </div>

      <div className="legal-section">
        <h2>8. Fees and Payment</h2>
        <p>
          Fees and payment terms are set out in your order form or separate commercial
          agreement with {legal.companyName}. Where no separate agreement exists, our standard
          rate card applies. Contact{' '}
          <a href={`mailto:${legal.contactEmail}`} className="legal-link">
            {legal.contactEmail}
          </a>{' '}
          for pricing information.
        </p>
      </div>

      <div className="legal-section">
        <h2>9. Suspension and Termination</h2>
        <p>
          <strong>9.1</strong> We may suspend or terminate your access to the Platform if:
        </p>
        <ul>
          <li>You breach these Terms and fail to remedy the breach within 14 days of notice</li>
          <li>We are required to do so by law</li>
          <li>You become insolvent or cease trading</li>
          <li>Continued access poses a security or legal risk to the Platform or other Members</li>
        </ul>
        <p>
          <strong>9.2</strong> You may close your account at any time by contacting{' '}
          <a href={`mailto:${legal.contactEmail}`} className="legal-link">
            {legal.contactEmail}
          </a>
          . Account closure does not entitle you to a refund of any fees paid.
        </p>
        <p>
          <strong>9.3</strong> On termination, we will retain your data for the period
          described in our Privacy Policy, after which it will be securely deleted.
        </p>
      </div>

      <div className="legal-section">
        <h2>10. Limitation of Liability</h2>
        <p>
          <strong>10.1</strong> To the maximum extent permitted by law, our total liability to
          you in connection with the Platform — whether in contract, tort (including
          negligence), or otherwise — shall not exceed the greater of £500 or the fees paid by
          you in the 12 months preceding the claim.
        </p>
        <p>
          <strong>10.2</strong> We are not liable for:
        </p>
        <ul>
          <li>Loss of profits, revenue, or business</li>
          <li>Loss or corruption of data</li>
          <li>Indirect or consequential loss of any kind</li>
          <li>Losses arising from your failure to comply with these Terms or applicable law</li>
          <li>Equipment quality, condition, fitness, compliance, or safety</li>
          <li>Delivery delays, failed deliveries, logistics issues, or downtime between Members</li>
          <li>Supplier, hirer, or partner performance, non-performance, or breach</li>
          <li>Inaccuracies, omissions, or outdated information supplied by Members</li>
          <li>Disputes, claims, penalties, or losses arising from contracts between Members</li>
        </ul>
        <p>
          <strong>10.3</strong> Nothing in these Terms excludes or limits liability for death
          or personal injury caused by negligence, fraud, or any other liability that cannot be
          excluded by law.
        </p>
      </div>

      <div className="legal-section">
        <h2>11. Indemnity</h2>
        <p>
          You agree to indemnify and hold us harmless from any claims, losses, or expenses
          (including reasonable legal fees) arising from: (a) your use of the Platform; (b)
          Your Content; or (c) your breach of these Terms or applicable law.
        </p>
      </div>

      <div className="legal-section">
        <h2>12. Governing Law and Disputes</h2>
        <p>
          These Terms are governed by the laws of England and Wales. Any dispute arising under
          these Terms shall be subject to the exclusive jurisdiction of the courts of England
          and Wales.
        </p>
      </div>

      <div className="legal-section">
        <h2>13. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes
          by email at least 30 days before they take effect. Continued use of the Platform
          after the effective date constitutes acceptance of the revised Terms.
        </p>
        <p>
          The current version and effective date are shown at the top of this document.
          Historical versions are available on request.
        </p>
      </div>

      <div className="legal-section">
        <h2>14. Contact</h2>
        <div className="legal-footer">
          <p><strong>{legal.companyName}</strong></p>
          <p>{legal.companyAddress}</p>
          <p>
            <a href={`mailto:${legal.contactEmail}`} className="legal-link">
              {legal.contactEmail}
            </a>
          </p>
          <p>Company No. {legal.companyNumber} (England and Wales)</p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
